import React from 'react';
import { Tab } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import DocumentsTab from './tabs/DocumentsTab';
import MessagesTab from './tabs/MessagesTab';
import ComplianceTab from './tabs/ComplianceTab';
import ActivityTab from './tabs/ActivityTab';

import { ProjectTask as Task } from '../../types/project';
export type { Task }; // For backward compatibility

interface TaskSidePanelProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function TaskSidePanel({ task, isOpen, onClose, projectId, onEdit, onDelete }: TaskSidePanelProps) {
  const tabs = [
    { name: 'Documents', component: DocumentsTab },
    { name: 'Messages', component: MessagesTab },
    { name: 'Compliance', component: ComplianceTab },
    { name: 'Activity', component: ActivityTab },
  ];

  return (
    <AnimatePresence>
      {isOpen && task && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40 md:hidden"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-y-0 right-0 z-50 w-full md:w-[600px] lg:w-[700px] xl:w-[800px] bg-white shadow-xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {task.name || task.title}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {task.status.charAt(0).toUpperCase() + task.status.slice(1).replace('_', ' ')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    title="Edit task"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={onDelete}
                    className="p-2 rounded-md text-red-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Delete task"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="ml-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <Tab.Group as="div" className="flex-1 flex flex-col overflow-hidden">
              <Tab.List className="flex px-6 space-x-8 border-b border-gray-200">
                {tabs.map((tab) => (
                  <Tab
                    key={tab.name}
                    className={({ selected }) =>
                      classNames(
                        'py-3 px-1 border-b-2 font-medium text-sm focus:outline-none',
                        selected
                          ? 'border-amber-500 text-amber-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      )
                    }
                  >
                    {tab.name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="flex-1 overflow-hidden">
                {tabs.map((tab, idx) => (
                  <Tab.Panel
                    key={idx}
                    className="h-full overflow-y-auto focus:outline-none"
                  >
                    <tab.component task={task} />
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}